import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock3 } from "lucide-react";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { ARTICLES, findArticle } from "@/data/articles";
import { useSEO } from "@/hooks/useSEO";

const ICONS = {};

export default function BlogPost() {
  const { slug } = useParams();
  const article = findArticle(slug);
  useSEO(
    article ? `${article.title} — Vowpin Global Blog` : "Vowpin Global Blog",
    article ? article.brief : "Insights on digital gifting and rewards from Vowpin Global."
  );
  if (!article) return <Navigate to="/blog" replace />;

  const recent = ARTICLES.filter((a) => a.slug !== slug).slice(0, 4);

  return (
    <main className="pt-24">
      <section className="relative overflow-hidden bg-white py-16 lg:py-20">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Link
              to="/blog"
              data-testid="post-back-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-[#64748b] transition-colors duration-200 hover:text-[#0816a8]"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
              All insights
            </Link>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-[#f4a900]/[0.12] px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-[#b87e00]">
                {article.tag}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-[#9aa3b5]">
                <Clock3 className="h-3.5 w-3.5" />
                {article.readTime}
              </span>
              <span className="text-xs text-[#9aa3b5]">Vowpin Editorial</span>
            </div>
            <h1 className="mt-6 max-w-3xl font-display text-3xl font-semibold leading-tight tracking-tight text-[#111827] sm:text-4xl lg:text-5xl" data-testid="post-title">
              {article.title}
            </h1>
            <p className="mt-5 max-w-2xl text-base font-medium text-[#0816a8]/80 md:text-lg">{article.brief}</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-[#e8ecf4] bg-[#f7f9fc] py-16">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1fr_340px] lg:px-10">
          {/* Article body */}
          <Reveal>
            <article className="glow-card rounded-[24px] p-10 sm:p-12" data-testid="post-body">
              {article.list && (
                <ol className="mt-10 space-y-4">
                  {article.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-4">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#f4a900] font-display text-xs font-bold text-[#3d2a00]">
                        {j + 1}
                      </span>
                      <p className="text-sm leading-relaxed text-[#374151] md:text-base">{item}</p>
                    </li>
                  ))}
                </ol>
              )}
              <div className="mt-10 space-y-6">
                {article.body.map((p, j) => (
                  <p key={j} className={`leading-relaxed md:text-lg ${j === 0 && !article.list ? "font-display text-xl text-[#374151]" : "text-base text-[#64748b]"}`}>
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-[#0816a8]/[0.04] p-6">
                <p className="font-display text-lg font-semibold text-[#111827]">
                  Ready to put this into practice?
                </p>
                <Link
                  to="/contact"
                  data-testid="post-talk-to-sales-btn"
                  className="group flex items-center gap-2 rounded-full bg-[#0816a8] px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#0a1bc4]"
                >
                  Talk to Sales
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          </Reveal>

          {/* Sidebar */}
          <aside className="space-y-6" data-testid="post-sidebar">
            <Reveal delay={0.1}>
              <div className="glow-card rounded-[20px] p-7">
                <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-[#c98a00]">Recent Articles</h3>
                <ul className="mt-5 space-y-5">
                  {recent.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/blog/${r.slug}`}
                        data-testid={`recent-${r.slug}`}
                        className="group block"
                      >
                        <span className="text-[11px] font-semibold uppercase tracking-widest text-[#0816a8]">{r.tag}</span>
                        <p className="mt-1.5 text-sm font-semibold leading-snug text-[#111827] transition-colors duration-200 group-hover:text-[#0816a8]">
                          {r.title}
                        </p>
                        <span className="mt-1 block text-xs text-[#9aa3b5]">{r.readTime}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/blog"
                  data-testid="sidebar-all-insights-link"
                  className="group mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[#0816a8] transition-colors duration-200 hover:text-[#0a1bc4]"
                >
                  View all insights
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="rounded-[20px] bg-[#0816a8] p-7">
                <h3 className="font-display text-lg font-semibold text-white">Explore the catalog</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#c7cef5]">
                  500+ global brands across 120+ countries, delivered instantly.
                </p>
                <Link
                  to="/products"
                  data-testid="sidebar-catalog-btn"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#f4a900] px-5 py-2.5 text-sm font-semibold text-[#3d2a00] transition-shadow duration-300 hover:shadow-[0_0_24px_rgba(244,169,0,0.45)]"
                >
                  Request Catalogue
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
